import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Toaster } from "../components/ui/toaster";
import { useToast } from "../hooks/use-toast";
import { ArrowRight, Sprout } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.includes("@") || !form.password) {
      toast({ title: "Check your email & password" });
      return;
    }
    setSubmitting(true);
    try {
      const u = await login(form);
      if (u.church_id) navigate("/dashboard");
      else navigate("/dashboard");
    } catch (err) {
      toast({
        title: "Sign in failed",
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-black text-white min-h-screen antialiased">
      <Navbar />
      <section className="pt-28 md:pt-36 pb-16 bg-[#04181a] min-h-[80vh] flex items-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-400/30 text-teal-200 px-3 py-1.5 rounded-full text-xs tracking-wide">
            <Sprout className="w-3.5 h-3.5" /> Welcome back
          </div>
          <h1 className="mt-5 text-3xl md:text-4xl font-semibold tracking-tight text-white leading-tight">
            Sign in to{" "}
            <em className="text-teal-300 font-serif font-normal">your hub.</em>
          </h1>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <label className="block text-sm font-medium text-white/85 mb-2">Email</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="you@church.org"
                className="h-11 bg-white/[0.05] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-teal-400 focus-visible:border-teal-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/85 mb-2">Password</label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="At least 8 characters"
                className="h-11 bg-white/[0.05] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-teal-400 focus-visible:border-teal-400"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-teal-400 hover:bg-teal-300 text-black font-semibold h-12 disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign in"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>

          <div className="mt-6 text-sm text-white/55 text-center">
            Don&apos;t have an account?{" "}
            <Link to="/signup" className="text-teal-300 hover:text-teal-200 underline underline-offset-2">
              Start your hub
            </Link>
          </div>
        </div>
      </section>
      <Footer />
      <Toaster />
    </div>
  );
};

export default LoginPage;
